import { Collection } from 'firestorter';

import CollectionName from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';

/**
 * The type of user activities.
 *
 * Redeclared here because there is no shared package between web and server.
 * The canonical definition is `UserActivityHistoryType` in
 * `system2-server/functions-22/src/userActivity/types.ts` — add new members
 * there first, and keep the string values identical, since they are what is
 * stored on the document.
 *
 * This list does not have to be complete for the tab to work: an event type
 * missing from it still renders, through the fallback in `getActivityContent`.
 * A member is needed only to give a type its own copy or put it in a filter
 * category.
 */
const UserActivityType = {
  LEAD_STATUS_CHANGE: 'LEAD_STATUS_CHANGE',
  LEAD_CALL_PLACED: 'LEAD_CALL_PLACED',
  LEAD_MSG: 'LEAD_MSG',
  SUBSCRIPTION_STATUS_CHANGE: 'SUBSCRIPTION_STATUS_CHANGE',
  WORKOUT_COMPLETED: 'WORKOUT_COMPLETED',
  CHECK_IN_SUBMITTED: 'CHECK_IN_SUBMITTED',
  HABIT_SUBMITTED: 'HABIT_SUBMITTED',
  PAYMENT_SUCCESSFUL: 'PAYMENT_SUCCESSFUL',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  PAYMENT_REFUNDED: 'PAYMENT_REFUNDED',
  SIGN_UP: 'SIGN_UP',
  POST_PAYMENT_FORM_SUBMIT: 'POST_PAYMENT_FORM_SUBMIT',

  // Subscription lifecycle and coach exercise overrides (S2-8894).
  CANCEL: 'CANCEL',
  RE_ONBOARD: 'RE_ONBOARD',
  SUBSCRIPTION_CHANGE: 'SUBSCRIPTION_CHANGE',
  SUBSCRIPTION_AMENDED: 'SUBSCRIPTION_AMENDED',
  EXERCISE_OVERRIDE_REMOVED: 'EXERCISE_OVERRIDE_REMOVED',
  EXERCISE_OVERRIDE_REPLACED: 'EXERCISE_OVERRIDE_REPLACED',

  // Meal plans and recipe feedback (S2-8843).
  MEAL_PLAN_ASSIGNED: 'MEAL_PLAN_ASSIGNED',
  RECIPE_LIKED: 'RECIPE_LIKED',
  RECIPE_DISLIKED: 'RECIPE_DISLIKED',
  RECIPE_FEEDBACK_RETRACTED: 'RECIPE_FEEDBACK_RETRACTED',

  // Workout assignments and check-in measurements (S2-8844).
  WORKOUT_ASSIGNED: 'WORKOUT_ASSIGNED',
  WORKOUT_PROGRAM_ASSIGNED: 'WORKOUT_PROGRAM_ASSIGNED',
  MEASUREMENT_RECORDED: 'MEASUREMENT_RECORDED',
};

/**
 * How many activity documents one query fetches.
 *
 * Matches the chat module's `CHANNEL_MESSAGE_PAGE_SIZE`, which drives the same
 * cursor pagination. Deliberately larger than the ten rows the tab reveals per
 * click, so most clicks cost no query at all.
 */
const ACTIVITY_PAGE_SIZE = 30;

/**
 * Class representing a user activity that holds the user activity data,
 * which is used to track the client activity history such as workout completion, check-in submission, etc.
 *
 * @class UserActivity
 * @extends BaseDocument
 */
class UserActivity extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.USER_ACTIVITY}/${id}`, opts);
  }

  /**
   * Get the type of activity
   * @return {string}
   */
  get type() {
    return this.data.type;
  }

  /**
   * Get the client this activity belongs to.
   *
   * Needed to tell a coach-programmed workout from one the client picked
   * themselves, where the event's `assignedBy` equals this id.
   * @return {string}
   */
  get userId() {
    return this.data.userId;
  }

  /**
   * Get the activity created date in UTC format
   * @return {Date}
   */
  get createdAt() {
    return this.data.createdAt.toDate();
  }

  /**
   * Get the activity data
   * @return {object|string}
   */
  get activityData() {
    return this.data.data;
  }

  /**
   * Get one page of a user's activities, newest first.
   *
   * Bounded deliberately: this used to fetch every activity document the client
   * had and paginate in memory, which grows without limit as the history log
   * gains event types. The caller holds the pages it has fetched and asks for
   * the next one, the same way the chat module pages messages.
   *
   * Scoped to one coach relationship (S2-8990), so a client held by two coaches
   * shows each of them only their own timeline.
   *
   * **Without `types` the query carries no `type` clause at all**, which is what
   * keeps an event type the server adds later visible here with no web change.
   * Pass `types` only to narrow to a filter category.
   *
   * Indexes: the unfiltered query uses `userActivity (userId ASC, coach ASC,
   * createdAt DESC)`; the filtered one needs `userActivity (userId ASC, coach
   * ASC, type ASC, createdAt DESC)`. Both are coach-scoped, so an index without
   * `coach` ahead of the sort field cannot serve either. A filtered query issued
   * before its index exists fails with `FAILED_PRECONDITION`.
   *
   * @param {string} userId - The user id
   * @param {string} coachId - The coach whose timeline to read
   * @param {object} [options]
   * @param {UserActivity | null} [options.startAfterDoc] - Last document of the
   *   previous page; omit for the first page.
   * @param {string[] | null} [options.types] - Restrict to these event types.
   *   Firestore caps `in` at 30 values, which every filter category is well
   *   under. Omit for every type.
   * @returns {Promise<Collection<UserActivity>>} One page, at most
   *   `ACTIVITY_PAGE_SIZE` documents. A short page means there are no more.
   */
  static async getActivitiesByUser(userId, coachId, options = {}) {
    const { startAfterDoc = null, types = null } = options;
    const collection = new Collection(CollectionName.USER_ACTIVITY, {
      createDocument: ({ id }, opts) => new UserActivity(id, opts),
      /*
        firestorter defaults to `Mode.Auto`, which opens an `onSnapshot` as soon
        as an observer reads the collection — so every page kept on screen would
        hold its own live listener for as long as the tab is open. Client history
        is a historical log: nothing in it changes while a coach reads it, and
        each page is fetched once, explicitly, below. Same pattern as
        `Model/CoachPod.js`.
      */
      mode: 'off',
      minimizeUpdates: true,
      query: (ref) => {
        let query = ref
          .where('userId', '==', userId)
          .where('coach', '==', coachId);

        if (types && types.length) {
          query = query.where('type', 'in', types);
        }

        query = query
          .orderBy('createdAt', 'desc')
          .limit(ACTIVITY_PAGE_SIZE);

        if (startAfterDoc) {
          query = query.startAfter(startAfterDoc.snapshot);
        }

        return query;
      },
    });
    await collection.fetch();
    return collection;
  }
}

export default UserActivity;
export {
  UserActivityType,
  ACTIVITY_PAGE_SIZE,
};
